import {
  Avatar,
  Box,
  Card,
  CardContent,
  Grid,
  Link,
  Typography,
} from "@mui/material";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Legend, Tooltip } from "chart.js";
import { formatDistance } from "date-fns";

import { Scrollbar } from "react-scrollbars-custom";

import { Tooltip as ChartToolTip } from "@mui/material";
import { ContributorsCardProps } from "../../lib/githubHooks";
import { InfoOutlined } from "@mui/icons-material";

ChartJS.register(ArcElement, Legend, Tooltip);

function formatShortDistance(date: string) {
  return date
    .replace(" seconds", "s")
    .replace(" second", "s")
    .replace(" minutes", "m")
    .replace(" minute", "m")
    .replace(" hours", "h")
    .replace(" hour", "h")
    .replace(" days", "d")
    .replace(" day", "d")
    .replace(" months", "m")
    .replace(" month", "m")
    .replace(" years", "y")
    .replace(" year", "y")
    .replace("about ", "")
    .replace("less than a minute", "<1m")
    .replace(" ago", " ago"); // optional – keeps " ago"
}

export interface TopContributorComponentProps {
  avatarUrl: string;
  username: string;
  commits: number;
}

const TopContributor = ({
  avatarUrl,
  username,
  commits,
}: TopContributorComponentProps) => (
  <Box
    display="flex"
    alignItems="center"
    justifyContent="space-between"
    width="100%"
    py={1}
    sx={{ background: "#F8F8F8" }}
  >
    <Box display="flex" alignItems="center">
      <Avatar
        src={avatarUrl}
        alt={username}
        sx={{ width: 32, height: 32, mr: 1 }}
      />
      <Link
        href={`https://github.com/${username}`}
        underline="hover"
        variant="body2"
        color="text.primary"
      >
        {username}
      </Link>
    </Box>
    <Typography
      variant="body2"
      color="text.secondary"
      // style={{ marginRight: 30 }}
    >
      {commits.toLocaleString()} commits
    </Typography>
  </Box>
);

const CommitItem = ({ commit }: any) => {
  const {
    author: { avatarUrl, user },
    message,
    committedDate,
    oid,
    repository: { nameWithOwner },
  } = commit;

  const username = user?.login || "unknown";
  const shortSha = oid.substring(0, 7);

  const date = new Date();

  const now_utc = date.toISOString().slice(0, -5);

  const formattedDate = formatShortDistance(
    formatDistance(committedDate.replace("Z", "") ?? 0, now_utc, {
      addSuffix: true,
    }).toString()
  );

  return (
    <Grid container sx={{ borderBottom: "1px solid #cfcfcf", pb: 1 }}>
      <Grid
        item
        xs={12}
        sm={12}
        md={3}
        lg={3}
        xl={3}
        sx={{
          order: { xs: 1, sm: 1, md: 3 },
          height: "36px",
          width: "100%",
        }}
        display={"flex"}
        direction={"row"}
        justifyContent={"space-between"}
        alignItems={"flex-start"}
      >
        <Box
          display={"flex"}
          flexDirection={"row"}
          alignItems={"flex-start"}
          sx={{ height: "40px" }}
          gap={"5px"}
        >
          <Avatar
            src={avatarUrl}
            alt={username}
            sx={{ width: 36, height: 36, mt: "-5px" }}
          />
          <Link
            href={`https://github.com/${nameWithOwner}`}
            target="_blank"
            rel="noopener"
            variant="body2"
          >
            {nameWithOwner}
          </Link>
        </Box>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ whiteSpace: "nowrap", marginRight: 5 }}
        >
          {formattedDate}
        </Typography>
      </Grid>
      <Grid
        item
        xs={12}
        sm={12}
        md={6}
        lg={7}
        xl={7}
        display={"flex"}
        direction={"row"}
        alignItems={"flex-start"}
        sx={{ order: { xs: 2, sm: 2, md: 1 } }}
      >
        <Typography
          variant="body1"
          fontWeight="medium"
          sx={{ wordBreak: "break-all" }}
        >
          {message}
        </Typography>
      </Grid>
      <Grid
        item
        xs={12}
        sm={12}
        md={3}
        lg={2}
        xl={2}
        display={"flex"}
        direction={"row"}
        alignItems={"flex-start"}
        sx={{ height: "36px", order: { xs: 3, sm: 3, md: 2 } }}
      >
        <Link
          href={`https://github.com/${nameWithOwner}/commit/${oid}`}
          target="_blank"
          rel="noopener"
          variant="body2"
        >
          {shortSha}
        </Link>
        {user?.login && (
          <>
            <Typography variant="body2" color="text.secondary">
              ·
            </Typography>
            <Link
              href={`https://github.com/${user.login}`}
              target="_blank"
              rel="noopener"
              variant="body2"
            >
              {user.login}
            </Link>
          </>
        )}
      </Grid>
    </Grid>
  );
};

// Main contributors card
const cardComponent = ({ title, data, type, info }: ContributorsCardProps) => {
  const newCount = 0;
  const activeCount = data - newCount;

  const ringData = {
    labels: ["New", "Active"],
    datasets: [
      {
        data: [newCount, activeCount],
        backgroundColor: ["#d0cfff", "rgb(159, 204, 46)"],
        borderWidth: 0,
        cutout: "95%",
      },
    ],
  };

  const ringOptions = {
    plugins: {
      legend: {
        display: false,
      },
    },
    maintainAspectRatio: false,
  };

  return (
    <>
      <Box
        sx={{
          width: "100%",
          borderRadius: 4,
          p: 2,
          border: ["Ring", "ListCommits"].includes(type)
            ? "1px solid #d2d2d3ff"
            : "none",
          boxSizing: "border-box",
        }}
      >
        <Box display="flex" alignItems="center" sx={{ gap: "5px" }}>
          <Typography variant="h6" gutterBottom>
            {title}
          </Typography>

          <ChartToolTip
            title={
              <>
                <Typography fontSize={16}>{info.desc}</Typography>
                <Typography fontSize={16}>{info.time}</Typography>
              </>
            }
          >
            <InfoOutlined
              fontSize="small"
              color="action"
              sx={{ mb: 1, cursor: "pointer" }}
            />
          </ChartToolTip>
        </Box>

        {type === "Ring" ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            sx={{ height: 460 }}
          >
            <Box sx={{ width: "100%", minHeight: 420, position: "relative" }}>
              <Doughnut data={ringData} options={ringOptions} />
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  textAlign: "center",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Total
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {data}
                </Typography>
              </Box>
            </Box>
            {/* <Box mt={4} width="100%">
            <Divider sx={{ mb: 5, borderBottomWidth: 3 }} />
            <Box display="flex" justifyContent="center" alignItems="center" gap={4}>
              <Box display="flex" alignItems="center" gap={1}>
                <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: '#d0cfff' }} />
                <Typography variant="caption">New</Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: '#0000ff' }} />
                <Typography variant="caption">Active</Typography>
              </Box>
            </Box>
          </Box> */}
          </Box>
        ) : type === "ListCommits" ? (
          <Card sx={{ border: "none", boxShadow: "none" }}>
            <CardContent sx={{ p: 0, maxHeight: 420, overflowY: "auto" }}>
              <Scrollbar
                style={{ height: 420 }}
                trackYProps={{
                  style: { backgroundColor: "transparent" },
                }}
                thumbYProps={{
                  style: {
                    backgroundColor: "rgba(54, 51, 51, 0.2)",
                    borderRadius: "6px",
                    width: "6px",
                  },
                }}
              >
                <Box
                  display="flex"
                  flexDirection="column"
                  gap={4}
                  sx={{ mt: 1 }}
                >
                  {Array.isArray(data) &&
                    data.map((commit, idx) => (
                      <CommitItem key={idx} commit={commit} />
                    ))}
                </Box>
              </Scrollbar>
            </CardContent>
          </Card>
        ) : (
          <Card sx={{ border: "none", boxShadow: "none", scrollbar: "none" }}>
            <CardContent
              sx={{
                scrollbar: "none",
                background: "#F8F8F8",
                padding: "0px",
              }}
            >
              <Box display="flex" flexDirection="column" gap={2}>
                {data &&
                  Object.entries(data).map(([username, contributorData]) => {
                    const { avatarUrl, commits } = contributorData as {
                      avatarUrl: string;
                      commits: number;
                    };
                    return (
                      <TopContributor
                        key={username}
                        avatarUrl={avatarUrl}
                        username={username}
                        commits={commits}
                      />
                    );
                  })}
              </Box>
            </CardContent>
          </Card>
        )}
      </Box>
    </>
  );
};

export default cardComponent;
